import { useCallback, useEffect, useRef, useState } from "react";
import { MobileNav, TOP_BAR_HEIGHT_PX } from "./MobileNav";
import { SlideContextProvider } from "./SlideContext";
import { useDeckViewTracking } from "./useDeckViewTracking";
import type { Deck } from "./types";

export function StoryPage({ deck }: { deck: Deck }) {
  const [navOpen, setNavOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  useDeckViewTracking(deck, currentIndex, "mobile");

  useEffect(() => {
    document.title = deck.title;
  }, [deck.title]);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0),
          );
        if (visible.length === 0) return;
        const idAttr = (visible[0].target as HTMLElement).dataset.slideIndex;
        if (idAttr) setCurrentIndex(Number(idAttr));
      },
      { threshold: [0.25, 0.5, 0.75] },
    );

    sectionRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [deck]);

  const scrollToSection = useCallback((i: number) => {
    sectionRefs.current[i]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    setNavOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-blue-950">
      <MobileNav
        slides={deck.slides}
        currentIndex={currentIndex}
        open={navOpen}
        onToggle={() => setNavOpen((v) => !v)}
        onSelect={scrollToSection}
      />

      <main
        className="flex flex-col gap-4 px-3 pb-4"
        style={{ paddingTop: TOP_BAR_HEIGHT_PX + 16 }}
      >
        {deck.slides.map((slide, i) => {
          const Slide = slide.Component;
          return (
            <section
              key={slide.id}
              ref={(el) => {
                sectionRefs.current[i] = el;
              }}
              data-slide-index={i}
              id={`slide-${i + 1}`}
              className="overflow-hidden rounded-lg bg-white shadow-xl"
              style={{ scrollMarginTop: TOP_BAR_HEIGHT_PX + 8 }}
            >
              <SlideContextProvider
                index={i}
                total={deck.slides.length}
                slides={deck.slides}
              >
                <Slide
                  title={slide.title}
                  index={i}
                  total={deck.slides.length}
                />
              </SlideContextProvider>
            </section>
          );
        })}
      </main>
    </div>
  );
}
